import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import * as Sentry from '@sentry/react';
import App from './app';
import ThemedClerkProvider from './clerk/themedClerkProvider';
import MobileAppBanner from './components/MobileAppBanner';
import { initMetaPixel } from './services/metaPixel';
// Must import before styles.scss so our --rdp-* overrides win the cascade.
import 'react-day-picker/style.css';
import 'react-toastify/dist/ReactToastify.css';
import './styles.scss';

// Disabled in dev to keep hot-reload errors out of quota; production builds
// always send. sendDefaultPii=true matches the privacy policy (Anthropic +
// Azure AI listed as processors) and lets us see IP / user agent on errors,
// which beats blind triage. User identity is set explicitly via Clerk in App.
//
// `environment` reads from VITE_SENTRY_ENVIRONMENT (set per-branch in the
// pipeline) so the dev SWA and prod SWA show as separate environments in
// Sentry, even though both branches build with `yarn build` (MODE=production).
// Falls back to MODE for local builds without the var set.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !!import.meta.env.VITE_SENTRY_DSN && import.meta.env.MODE === 'production',
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
  sendDefaultPii: true,
  tracesSampleRate: 0.1,
  integrations: [Sentry.browserTracingIntegration()],
});

// Capture genuine failures (5xx, network, parse) but skip 4xx — those are
// expected validation/auth/not-found responses the UI already shows as toasts.
// httpError() in apiFetch.js attaches err.status, so we can branch cleanly.
function reportApiError(err) {
  const status = err?.status;
  if (status && status >= 400 && status < 500) return;
  Sentry.captureException(err);
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: reportApiError }),
  mutationCache: new MutationCache({ onError: reportApiError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Meta Pixel: no-op unless VITE_META_PIXEL_ID is set AND the visitor granted
// Marketing consent on the marketing site (shared .marginpal.co.uk cookie).
// Must run before React renders so the Stripe success-redirect URL is read
// before the router navigates away from it.
initMetaPixel();

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY');
}

const IS_SATELLITE = import.meta.env.VITE_CLERK_IS_SATELLITE === 'true';
const CLERK_DOMAIN = import.meta.env.VITE_CLERK_DOMAIN;
const CLERK_SATELLITE_DOMAIN = import.meta.env.VITE_CLERK_SATELLITE_DOMAIN;

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. The team has been notified.</p>}>
      <MobileAppBanner />
      <BrowserRouter>
        <ThemedClerkProvider
          publishableKey={PUBLISHABLE_KEY}
          isSatellite={IS_SATELLITE}
          domain={CLERK_SATELLITE_DOMAIN}
          signInUrl={IS_SATELLITE ? `https://accounts.${CLERK_DOMAIN}/sign-in` : '/sign-in'}
          signUpUrl={IS_SATELLITE ? `https://accounts.${CLERK_DOMAIN}/sign-up` : '/sign-up'}
          afterSignOutUrl={IS_SATELLITE ? `https://accounts.${CLERK_DOMAIN}/sign-in` : '/sign-in'}
          signInForceRedirectUrl="/"
        >
          <QueryClientProvider client={queryClient}>
            <App />
            {/* `theme="colored"` gives each toast a background based on its
                type (green success, red error) — reads equally well on both
                the light canvas and the dark slate-900 shell. */}
            <ToastContainer position="top-right" autoClose={3000} theme="colored" />
          </QueryClientProvider>
        </ThemedClerkProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
